import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { LibraryManager, ResourceStatus, ResourceType } from 'libs.library_manager';
import { useLocalizeMessage, useToastNotification } from 'libs.nucleus';
import { ApiClientService, AuthContext, useApiClient } from 'libs.react_internal';
import { getNextCloneName } from '../../../utils/clone_name.utils';
import { deduplicateResourcesByKey } from '../../../utils/deduplicate_by_key.utils';
/**
 * Hook for managing dashboard resources.
 * Mirrors useCharts — all CRUD via LibraryManager with REPORTING_DASHBOARD.
 */
export const useDashboards = () => {
    const { entityId } = useContext(AuthContext);
    const libraryClient = useApiClient(ApiClientService.LIBRARY);
    const translate = useLocalizeMessage();
    const { addNotification } = useToastNotification();
    const [dashboards, setDashboards] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    // Refs to avoid callback recreation
    const libraryClientRef = useRef(libraryClient);
    const entityIdRef = useRef(entityId);
    const translateRef = useRef(translate);
    const addNotificationRef = useRef(addNotification);
    useEffect(() => {
        libraryClientRef.current = libraryClient;
        entityIdRef.current = entityId;
        translateRef.current = translate;
        addNotificationRef.current = addNotification;
    }, [libraryClient, entityId, translate, addNotification]);
    /** Fetch all dashboards, optionally including archived */
    const fetchDashboards = useCallback(async (showArchived = false, status, search) => {
        setIsLoading(true);
        try {
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const response = await libraryManager.getResources(ResourceType.REPORTING_DASHBOARD, {
                entityId: entityIdRef.current,
                active: showArchived ? 'false' : 'true',
                defaultLibrary: true,
                ...(status ? { status } : {}),
                ...(search ? { search } : {}),
            });
            const result = response.data || [];
            // SSP-4186: collapse `key` chains to a single row (highest version) so that
            // partial version-upgrade workflows (or any state that leaves multiple active
            // resources under the same `key`) do not surface as duplicate list entries.
            // The "Show archived" view still shows every version because auditing across
            // an entire chain requires the full history.
            setDashboards(showArchived ? result : deduplicateResourcesByKey(result));
        }
        catch {
            addNotificationRef.current({
                title: translateRef.current('Error fetching dashboards'),
                subtitle: translateRef.current('Failed to load dashboards'),
                type: 'error',
            });
            setDashboards([]);
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    /** Fetch a single dashboard by ID */
    const fetchDashboardById = useCallback(async (dashboardId) => {
        setIsLoading(true);
        try {
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const resource = await libraryManager.getResourceById(dashboardId, ResourceType.REPORTING_DASHBOARD);
            return resource;
        }
        catch {
            addNotificationRef.current({
                title: translateRef.current('Error fetching dashboard'),
                subtitle: translateRef.current('Failed to load dashboard details'),
                type: 'error',
            });
            return undefined;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    /** Create a new dashboard */
    const createDashboard = useCallback(async (form) => {
        setIsLoading(true);
        try {
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            // Build chart refs matching the data_reports ChartConfig format
            const charts = form.charts.map((chart, index) => ({
                id: chart.resourceKey,
                libraryResourceId: chart.resourceId,
                libraryResourceKey: chart.resourceKey,
                name: chart.name,
                title: chart.title,
                type: chart.type,
                position: index + 1,
            }));
            const createRequest = {
                name: form.name,
                type: ResourceType.REPORTING_DASHBOARD,
                status: ResourceStatus.DRAFT,
                entityId: entityIdRef.current?.toString(),
                data: {
                    name: form.name,
                    description: form.description || '',
                    charts,
                    cubeConfigId: form.cubeConfigId,
                    cubeConfigKey: form.cubeConfigKey,
                },
                metadata: {
                    title: form.name,
                },
            };
            const resource = await libraryManager.createResource(createRequest, ResourceType.REPORTING_DASHBOARD);
            const newDashboard = resource;
            addNotificationRef.current({
                title: translateRef.current('Dashboard created successfully'),
                type: 'success',
            });
            setDashboards((prev) => [...prev, newDashboard]);
            return newDashboard;
        }
        catch (error) {
            addNotificationRef.current({
                title: translateRef.current('Error creating dashboard'),
                subtitle: translateRef.current('Failed to create dashboard'),
                type: 'error',
            });
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    /**
     * Clone an existing dashboard.
     *
     * When `options.silent` is true the hook suppresses its built-in success/error
     * toasts. This is used by compound flows (SSP-4186) that need to emit a single
     * outcome toast covering multiple hook calls.
     */
    const cloneDashboard = useCallback(async (sourceDashboard, option = 'CLONE', overrides, options) => {
        setIsLoading(true);
        try {
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const clonedName = overrides?.name || sourceDashboard.name;
            const sourceData = (sourceDashboard.data || {});
            const clonedDescription = overrides?.description ?? sourceData.description ?? '';
            const createRequest = {
                name: clonedName,
                type: ResourceType.REPORTING_DASHBOARD,
                status: ResourceStatus.DRAFT,
                entityId: entityIdRef.current?.toString(),
                data: {
                    ...sourceData,
                    name: clonedName,
                    description: clonedDescription,
                    ...(overrides?.cubeConfigId ? { cubeConfigId: overrides.cubeConfigId } : {}),
                    ...(overrides?.cubeConfigKey ? { cubeConfigKey: overrides.cubeConfigKey } : {}),
                },
                metadata: {
                    title: clonedName,
                },
            };
            if (option === 'CREATE_VERSION') {
                createRequest.key = sourceDashboard.key || sourceDashboard.id;
            }
            const resource = await libraryManager.createResource(createRequest, ResourceType.REPORTING_DASHBOARD);
            const cloned = resource;
            if (!options?.silent) {
                addNotificationRef.current({
                    title: translateRef.current('Dashboard cloned successfully'),
                    type: 'success',
                });
            }
            // SSP-4186: update-or-append by id so a follow-up fetch that already
            // returned the new resource does not double it in local state.
            setDashboards((prev) => (prev.some((d) => d.id === cloned.id) ? prev : [...prev, cloned]));
            return cloned;
        }
        catch (error) {
            if (!options?.silent) {
                addNotificationRef.current({
                    title: translateRef.current('Error cloning dashboard'),
                    subtitle: translateRef.current('Failed to clone dashboard'),
                    type: 'error',
                });
            }
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    /** Update dashboard details (name, description, dataset) */
    const updateDashboardDetails = useCallback(async (dashboardId, form) => {
        setIsLoading(true);
        try {
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            // Fetch existing dashboard to preserve all data fields (PUT replaces entire object)
            const existing = await libraryManager.getResourceById(dashboardId, ResourceType.REPORTING_DASHBOARD);
            const existingData = existing?.data || {};
            const resource = await libraryManager.updateResource(dashboardId, ResourceType.REPORTING_DASHBOARD, {
                name: form.name,
                metadata: { title: form.name },
                data: {
                    ...existingData,
                    name: form.name,
                    description: form.description,
                    cubeConfigId: form.cubeConfigId,
                    cubeConfigKey: form.cubeConfigKey,
                    ...(form.charts !== undefined ? { charts: form.charts } : {}),
                },
            });
            const updated = resource;
            setDashboards((prev) => prev.map((d) => (d.id === dashboardId ? updated : d)));
            addNotificationRef.current({
                title: translateRef.current('Dashboard details updated'),
                type: 'success',
            });
            return updated;
        }
        catch (error) {
            addNotificationRef.current({
                title: translateRef.current('Error updating dashboard details'),
                subtitle: translateRef.current('Failed to update dashboard details'),
                type: 'error',
            });
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    /** Update dashboard data config (charts, layout) */
    const updateDashboardData = useCallback(async (dashboardId, data) => {
        setIsLoading(true);
        try {
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const existing = await libraryManager.getResourceById(dashboardId, ResourceType.REPORTING_DASHBOARD);
            const existingData = (existing?.data || {});
            const resource = await libraryManager.updateResource(dashboardId, ResourceType.REPORTING_DASHBOARD, {
                data: {
                    ...existingData,
                    ...data,
                },
            });
            const updated = resource;
            setDashboards((prev) => prev.map((d) => (d.id === dashboardId ? updated : d)));
            addNotificationRef.current({
                title: translateRef.current('Dashboard saved successfully'),
                type: 'success',
            });
            return updated;
        }
        catch (error) {
            addNotificationRef.current({
                title: translateRef.current('Error saving dashboard'),
                subtitle: translateRef.current('Failed to save dashboard configuration'),
                type: 'error',
            });
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    /** Transition dashboard status */
    const transitionStatus = useCallback(async (dashboardId, newStatus) => {
        setIsLoading(true);
        try {
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const resource = await libraryManager.updateResource(dashboardId, ResourceType.REPORTING_DASHBOARD, {
                status: newStatus,
            });
            const updated = resource;
            setDashboards((prev) => prev.map((d) => (d.id === dashboardId ? updated : d)));
            addNotificationRef.current({
                title: translateRef.current('Dashboard status updated'),
                type: 'success',
            });
            return updated;
        }
        catch (error) {
            addNotificationRef.current({
                title: translateRef.current('Error updating dashboard status'),
                subtitle: translateRef.current('Failed to change dashboard status'),
                type: 'error',
            });
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    /**
     * Archive a dashboard.
     *
     * When `options.silent` is true the hook suppresses its built-in success/error
     * toasts so compound flows (SSP-4186) can emit a single summary toast.
     */
    const archiveDashboard = useCallback(async (dashboardId, options) => {
        setIsLoading(true);
        try {
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            await libraryManager.archiveResource(dashboardId, ResourceType.REPORTING_DASHBOARD);
            setDashboards((prev) => prev.map((d) => (d.id === dashboardId ? { ...d, active: false } : d)));
            if (!options?.silent) {
                addNotificationRef.current({
                    title: translateRef.current('Dashboard archived successfully'),
                    type: 'success',
                });
            }
        }
        catch (error) {
            if (!options?.silent) {
                addNotificationRef.current({
                    title: translateRef.current('Error archiving dashboard'),
                    subtitle: translateRef.current('Failed to archive dashboard'),
                    type: 'error',
                });
            }
            throw error;
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    /** Check if a dashboard name is unique */
    const checkDashboardNameUnique = useCallback(async (nameToCheck, excludeId) => {
        try {
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const response = await libraryManager.getResources(ResourceType.REPORTING_DASHBOARD, {
                entityId: entityIdRef.current,
                active: 'true',
                name: nameToCheck,
            });
            const matches = response.data || [];
            const duplicate = matches.find((d) => d.name.toLowerCase() === nameToCheck.toLowerCase() && d.id !== excludeId);
            return !duplicate;
        }
        catch {
            return true;
        }
    }, []);
    /** Fetch available datasets (cube configs) — only PUBLISHED (Ready) */
    const fetchDatasets = useCallback(async () => {
        try {
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const response = await libraryManager.getResources(ResourceType.CUBE_CONFIG, {
                entityId: entityIdRef.current,
                active: 'true',
                status: ResourceStatus.PUBLISHED,
            });
            const datasets = response.data || [];
            return datasets.map((ds) => ({
                id: ds.id,
                key: ds.key,
                name: ds.name,
                status: ds.status,
            }));
        }
        catch {
            addNotificationRef.current({
                title: translateRef.current('Error fetching datasets'),
                subtitle: translateRef.current('Failed to load available datasets'),
                type: 'error',
            });
            return [];
        }
    }, []);
    /** Fetch PUBLISHED charts filtered by dataset (cubeConfigKey) — for chart selection */
    const fetchPublishedCharts = useCallback(async (cubeConfigKey) => {
        try {
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const response = await libraryManager.getResources(ResourceType.REPORTING_CHART, {
                entityId: entityIdRef.current,
                status: ResourceStatus.PUBLISHED,
            });
            const allCharts = response.data || [];
            return allCharts.filter((chart) => {
                if (chart.active === false) {
                    return false;
                }
                const chartConfigKey = chart.cubeConfigKey || chart.data?.cubeConfigKey;
                return chartConfigKey === cubeConfigKey;
            });
        }
        catch {
            addNotificationRef.current({
                title: translateRef.current('Error fetching charts'),
                subtitle: translateRef.current('Failed to load available charts'),
                type: 'error',
            });
            return [];
        }
    }, []);
    /** Generate a unique clone name by querying existing dashboards with a partial name match */
    const generateUniqueCloneName = useCallback(async (baseName) => {
        try {
            const libraryManager = new LibraryManager({ axiosInstance: libraryClientRef.current });
            const response = await libraryManager.getResources(ResourceType.REPORTING_DASHBOARD, {
                entityId: entityIdRef.current,
                active: 'true',
                name: baseName,
            });
            const existingNames = (response.data || []).map((d) => d.name);
            return getNextCloneName(baseName, existingNames);
        }
        catch {
            return `${baseName} (Clone)`;
        }
    }, []);
    return {
        dashboards,
        isLoading,
        fetchDashboards,
        fetchDashboardById,
        createDashboard,
        cloneDashboard,
        updateDashboardDetails,
        updateDashboardData,
        transitionStatus,
        archiveDashboard,
        checkDashboardNameUnique,
        generateUniqueCloneName,
        fetchDatasets,
        fetchPublishedCharts,
    };
};
